import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { dateMatchesEventSchedule, MONTHSTR_FMT, normalizeHm, resolvedEventBookingType, TDR } from 'tdr-common';
import { useCalendarAvailability } from '../api/availability/availability';
import { getServiceTimeslots } from '../api/getServiceTimeslots';
import { findFirstAvailableTime, findFirstAvailableTimeInWindow } from '../helpers/getFirstAvailableDate';
import { getEventCalendarTables, resolveEventBookNowGroupSize } from '../helpers/resolveEventBookNowGroupSize';

const MAX_SLOT_SCAN_DATES = 20;
const CALENDAR_SCAN_WINDOW = 3;
const TIMESLOTS_CACHE_SETTINGS = {
	staleTime: 2 * 60 * 1000,
	gcTime: 5 * 60 * 1000
};

export type EventAvailabilityResult = {
	firstDate: string | null;
	firstTime: string | null;
	isLoading: boolean;
	hasNoAvailability: boolean;
};

/**
 * Non-recurring events carry their own fixed date(s), which can fall well outside a rolling
 * "next 3 months from today" window (e.g. a New Years Eve event booked in August for a Dec 31
 * date). Scanning the wrong months means the event's actual date never gets fetched, so it
 * always reads as sold out. Anchor the scan to the event's configured date(s) instead of "today"
 * whenever one is set; fall back to the rolling window for events with no fixed date (permanent,
 * recurring, or a non-recurring event with neither `dates` nor `betweenDates` configured).
 */
export function getNonRecurringScanMonthKeys(event: TDR.Event, zone: string): string[] | null {
	if ((event.eventSchedule ?? 'non-recurring') !== 'non-recurring') {
		return null;
	}

	const v = event.condition?.value;

	const dates = v?.dates;
	if (dates?.length) {
		const monthKeySet = new Set<string>();
		for (const dateStr of dates) {
			const parsed = DateTime.fromFormat(dateStr.trim(), 'MMMM dd, yyyy', { zone });
			const resolved = parsed.isValid
				? parsed
				: DateTime.fromJSDate(new Date(dateStr)).setZone(zone, { keepLocalTime: true });
			if (resolved.isValid) {
				monthKeySet.add(resolved.startOf('month').toFormat(MONTHSTR_FMT));
			}
		}
		const sortedKeys = Array.from(monthKeySet).sort();
		// Only 3 calendar queries are available (q0-q2 below); take the earliest months.
		return sortedKeys.length > 0 ? sortedKeys.slice(0, CALENDAR_SCAN_WINDOW) : null;
	}

	const between = v?.betweenDates;
	if (between?.length === 2) {
		const start = DateTime.fromJSDate(new Date(between[0])).setZone(zone, { keepLocalTime: true }).startOf('month');
		const end = DateTime.fromJSDate(new Date(between[1])).setZone(zone, { keepLocalTime: true }).startOf('month');
		if (!start.isValid || !end.isValid) {
			return null;
		}
		// Only scan months the range actually spans, capped by the 3 available query slots
		// (q0-q2 below) — an open-ended or multi-year range still can't exceed that budget.
		const keys: string[] = [];
		for (let cursor = start; cursor <= end && keys.length < CALENDAR_SCAN_WINDOW; cursor = cursor.plus({ months: 1 })) {
			keys.push(cursor.toFormat(MONTHSTR_FMT));
		}
		return keys.length > 0 ? keys : null;
	}

	return null;
}

function mergeDayAvailabilityMaps(
	...sources: (Record<string, TDR.Availability.dayAvailability> | Map<string, TDR.Availability.dayAvailability> | undefined)[]
): Record<string, TDR.Availability.dayAvailability> {
	const out: Record<string, TDR.Availability.dayAvailability> = {};
	for (const src of sources) {
		if (!src) {
			continue;
		}
		const entries = src instanceof Map ? Array.from(src.entries()) : Object.entries(src);
		for (const [k, v] of entries) {
			const prev = out[k];
			if (!prev || v.available > prev.available) {
				out[k] = v;
			}
		}
	}
	return out;
}

function listScheduleMatchingDates(
	merged: Record<string, TDR.Availability.dayAvailability>,
	event: TDR.Event,
	todayIsoInZone: string
): string[] {
	const keys = Object.keys(merged).sort((a, b) => a.localeCompare(b));
	const dates: string[] = [];
	for (const dateStr of keys) {
		if (dateStr.localeCompare(todayIsoInZone) < 0) {
			continue;
		}
		const dayAvail = merged[dateStr];
		if (!dayAvail || dayAvail.available <= 0) {
			continue;
		}
		if (!dateMatchesEventSchedule(dateStr, event)) {
			continue;
		}
		dates.push(dateStr);
	}
	return dates;
}

function resolveTimeFromSlots(
	slotsData: TDR.Availability.getServicesOutput | undefined,
	evKind: 'table' | 'param',
	paramHasTimeConstraint: boolean,
	betweenStart: string | undefined,
	betweenEnd: string | undefined
): string | undefined {
	if (!slotsData) {
		return undefined;
	}

	if (evKind === 'param' && paramHasTimeConstraint && betweenStart !== undefined) {
		return findFirstAvailableTimeInWindow(slotsData, betweenStart, betweenEnd ?? betweenStart);
	}

	return findFirstAvailableTime(slotsData);
}

/**
 * Computes the first ISO date/time that both matches the event condition and passes
 * calendar + timeslot checks (using the event's guest count, same as the parameters page).
 */
export function useEventAvailability(
	event: TDR.Event,
	tables: TDR.Table[],
	restaurantId: string,
	timezone?: string | null,
	disabled = false
): EventAvailabilityResult {
	const evKind = resolvedEventBookingType(event);
	const linkedTable = useMemo(
		() =>
			evKind === 'table' && event.linkedTableSlug
				? tables.find((t) => t.slug === event.linkedTableSlug)
				: undefined,
		[evKind, event.linkedTableSlug, tables]
	);

	const calendarTables = useMemo(
		() => getEventCalendarTables(event, tables, linkedTable),
		[event, tables, linkedTable]
	);

	const calendarsSkipped =
		disabled || !restaurantId || calendarTables.length === 0 || (evKind === 'table' && !linkedTable);

	const zone = timezone && timezone.trim() !== '' ? timezone : 'UTC';

	const monthKeys = useMemo(() => {
		const eventMonthKeys = getNonRecurringScanMonthKeys(event, zone);
		if (eventMonthKeys) {
			return eventMonthKeys;
		}
		const now = DateTime.now().setZone(zone).startOf('month');
		return [0, 1, 2].map((i) => now.plus({ months: i }).toFormat(MONTHSTR_FMT));
	}, [event, zone]);

	const betweenStart = normalizeHm(event.condition?.value?.betweenTimes?.[0]);
	const betweenEnd = normalizeHm(event.condition?.value?.betweenTimes?.[1]);
	const paramHasTimeConstraint = evKind === 'param' && betweenStart !== undefined;

	const q0 = useCalendarAvailability(
		{ tables: calendarTables, month: monthKeys[0], restaurantId },
		false,
		calendarsSkipped
	);

	const q1 = useCalendarAvailability(
		{ tables: calendarTables, month: monthKeys[1], restaurantId },
		false,
		calendarsSkipped
	);

	const q2 = useCalendarAvailability(
		{ tables: calendarTables, month: monthKeys[2], restaurantId },
		false,
		calendarsSkipped
	);

	const todayIsoInZone = useMemo(() => DateTime.now().setZone(zone).toISODate() ?? '', [zone]);

	// A slot with no month (event needs fewer than 3 calendar months) is disabled and will never
	// fetch, so it's trivially "done" rather than perpetually loading/unfetched.
	const monthNeeded = [!!monthKeys[0], !!monthKeys[1], !!monthKeys[2]];

	const calendarLoading =
		calendarsSkipped ? false :
			(monthNeeded[0] && q0.isFetching) ||
			(monthNeeded[1] && q1.isFetching) ||
			(monthNeeded[2] && q2.isFetching);

	const allCalFetched =
		calendarsSkipped || (
			(!monthNeeded[0] || (q0.isFetched && !q0.isFetching)) &&
			(!monthNeeded[1] || (q1.isFetched && !q1.isFetching)) &&
			(!monthNeeded[2] || (q2.isFetched && !q2.isFetching))
		);

	const mergedAvailability = useMemo(
		() =>
			calendarsSkipped
				? {}
				: mergeDayAvailabilityMaps(
					q0.data?.calendarAvailability,
					q1.data?.calendarAvailability,
					q2.data?.calendarAvailability
				),
		[
			calendarsSkipped,
			q0.data?.calendarAvailability,
			q1.data?.calendarAvailability,
			q2.data?.calendarAvailability
		]
	);

	const candidateDates = useMemo(() => {
		if (!allCalFetched || calendarsSkipped || !todayIsoInZone) {
			return [];
		}
		return listScheduleMatchingDates(mergedAvailability, event, todayIsoInZone);
	}, [allCalFetched, calendarsSkipped, mergedAvailability, event, todayIsoInZone]);

	const tableGroupSize = resolveEventBookNowGroupSize(event, tables, linkedTable);

	const needsSlotScan =
		!calendarsSkipped
		&& ((evKind === 'table' && !!linkedTable?.id) || evKind === 'param');

	const slotsTableIds = useMemo(() => {
		if (evKind === 'table' && linkedTable?.id) {
			return [linkedTable.id];
		}
		return calendarTables.map((t) => t.id);
	}, [evKind, linkedTable?.id, calendarTables]);

	const datesToScan = useMemo(
		() => (needsSlotScan ? candidateDates.slice(0, MAX_SLOT_SCAN_DATES) : []),
		[needsSlotScan, candidateDates]
	);

	const slotQueries = useQueries({
		queries: datesToScan.map((dateStr) => ({
			queryKey: ['serviceTimeslots', {
				tableIds: slotsTableIds,
				dateStr,
				restaurantId,
				options: { groupSize: tableGroupSize }
			}],
			queryFn: () => getServiceTimeslots({
				tableIds: slotsTableIds,
				dateStr,
				restaurantId,
				options: { groupSize: tableGroupSize }
			}),
			enabled: needsSlotScan && !!restaurantId && slotsTableIds.length > 0,
			...TIMESLOTS_CACHE_SETTINGS
		}))
	});

	const slotQueryStatuses = slotQueries
		.map((query) => `${query.isFetched}:${query.isError}:${query.dataUpdatedAt}`)
		.join('|');

	const { firstDate, firstTimeFromSlots } = useMemo(() => {
		if (!allCalFetched || calendarsSkipped) {
			return { firstDate: null as string | null, firstTimeFromSlots: undefined as string | undefined };
		}

		if (!needsSlotScan) {
			return {
				firstDate: candidateDates[0] ?? null,
				firstTimeFromSlots: undefined
			};
		}

		for (let i = 0; i < datesToScan.length; i++) {
			const dateStr = datesToScan[i];
			const query = slotQueries[i];
			if (!query?.isFetched || query.isError) {
				continue;
			}
			const time = resolveTimeFromSlots(
				query.data,
				evKind,
				paramHasTimeConstraint,
				betweenStart,
				betweenEnd
			);
			if (time !== undefined) {
				return { firstDate: dateStr, firstTimeFromSlots: time };
			}
		}

		return { firstDate: null, firstTimeFromSlots: undefined };
	}, [
		allCalFetched,
		calendarsSkipped,
		needsSlotScan,
		candidateDates,
		datesToScan,
		slotQueryStatuses,
		evKind,
		paramHasTimeConstraint,
		betweenStart,
		betweenEnd
	]);

	const firstTime: string | null = useMemo(() => firstTimeFromSlots ?? null, [firstTimeFromSlots]);

	const slotScanPending =
		needsSlotScan
		&& datesToScan.length > 0
		&& slotQueries.some((q) => q.isFetching || (!q.isFetched && !q.isError));

	const isLoading = calendarLoading || slotScanPending;

	const unavailableAfterAvailabilityFetch = useMemo(() => {
		if (calendarsSkipped) {
			return true;
		}
		if (!allCalFetched) {
			return false;
		}
		if (needsSlotScan) {
			if (slotScanPending) {
				return false;
			}
			return !firstDate || firstTimeFromSlots === undefined;
		}
		return candidateDates.length === 0;
	}, [
		calendarsSkipped,
		allCalFetched,
		needsSlotScan,
		slotScanPending,
		firstDate,
		firstTimeFromSlots,
		candidateDates.length
	]);

	const hasNoAvailability = !isLoading && unavailableAfterAvailabilityFetch;

	return {
		firstDate,
		firstTime,
		isLoading,
		hasNoAvailability
	};
}
